import React, { FC } from 'react';
import Badge from '@atlaskit/badge';
import EmptyState from '@atlaskit/empty-state';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Incidents } from '@/components/composed';
import { KeyValue, DetailButtonDialog, ItemHeader } from '@/components/primitive';
import { useFetchErrorController } from '@/controllers';
import { spaceTopCompact } from '@/styles';
import { apiFetch } from '@/services';
import { querySpec } from '@/services/api-service/api-queries';

interface PastIncidentsProps {
  publicId: API.Capability['publicId'];
}

const PastIncidents: FC<PastIncidentsProps> = ({ publicId }) => {
  const { onError } = useFetchErrorController();
  const { queryKey, path } = querySpec('hot.search.{id}', { id: publicId });
  const { data, isInitialLoading: isLoading } = useQuery(queryKey, () => apiFetch(path), {
    onError,
  });

  const content: [] = (!isLoading && data?.content) || [];

  return (
    <DetailButtonDialog
      title={
        <KeyValue title="See Past Incidents">
          <Badge max={Infinity}>{content.length}</Badge>
        </KeyValue>
      }
      isLoading={isLoading}
    >
      <div>
        <ItemHeader>Past Incidents</ItemHeader>
        <div css={spaceTopCompact}>
          {content.length ? (
            <Incidents data={content} isLoading={isLoading} size="compact" />
          ) : (
            <EmptyState
              header="No previous Incidents were associated"
              description={
                <>
                  This Capability has no SLOs previously associated with Incidents.
                  <br />
                  <Link
                    target="_blank"
                    to={{
                      pathname:
                        'https://developer.atlassian.com/platform/slos/tome/hot-association/',
                    }}
                  >
                    See our docs
                  </Link>{' '}
                  for more info about associating SLOs with incidents.
                </>
              }
              imageUrl="/assets/generic-empty-state.svg"
            />
          )}
        </div>
      </div>
    </DetailButtonDialog>
  );
};

export default PastIncidents;
